@if (editable) {
  <div class="space-y-6">
    <!-- Financial Information Form -->
    <div class="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div class="mb-5">
        <h3 class="text-lg font-semibold text-[#1E2A78]">
          {{ 'FINANCIAL_DATA.FINANCIAL_INFORMATION' | translate }}
        </h3>

        <p class="text-sm text-gray-500 mt-1">
          {{ 'FINANCIAL_DATA.FINANCIAL_INFORMATION_HINT' | translate }}
        </p>
      </div>

      <app-dynamic-form
        [fields]="demographicFields"
        [model]="demographicModel"
        (formChange)="onFormChange($event)"
        (formSubmit)="onSubmit($event)"
      >
      </app-dynamic-form>
    </div>

    <!-- Financial Data -->
    <div class="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      <!-- Header -->
      <div
        class="border-b border-gray-100 px-6 py-5"
      >
        <div
          class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <div>
            <h3 class="text-lg font-semibold text-[#1E2A78]">
              {{ 'FINANCIAL_DATA.TITLE' | translate }}
            </h3>

            <p class="mt-1 text-sm text-gray-500">
              {{ 'FINANCIAL_DATA.TITLE_HINT' | translate }}
            </p>
          </div>

          <div class="flex items-center gap-2">
            <select
              [(ngModel)]="selectedYear"
              class="rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
            >
              <option value="">
                {{ 'FINANCIAL_DATA.SELECT_YEAR' | translate }}
              </option>

              @for (year of availableYears; track year) {
                <option
                  [value]="year"
                  [disabled]="years.includes(year)"
                >
                  {{ year }}
                </option>
              }
            </select>

            <button
              type="button"
              (click)="addYear()"
              class="rounded-lg bg-[#1E2A78] px-4 py-2 text-sm text-white transition hover:bg-[#16205e]"
            >
              + {{ 'FINANCIAL_DATA.ADD_YEAR' | translate }}
            </button>

            <app-excel-upload
              [sheetName]="'Balance Sheet'"
              [startMarker]="'Balance Sheet'"
              [endMarkers]="['Income Statement', 'Income', 'Profit and Loss', 'Profitability']"
              [sections]="financialSections"
                  (parsed)="onExcelParsed($event)"
            ></app-excel-upload>
          </div>
        </div>

        <!-- File Upload / Import Message -->
        @if (fileUploadError) {
          <div
            class="mt-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-600"
          >
            {{ fileUploadError }}
          </div>
        }

        <!-- Global Year-wise Audited / Unaudited -->
        @if (years.length) {
          <div
            class="mt-5 rounded-2xl border border-indigo-100 bg-indigo-50/60 p-4"
          >
            <div
              class="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <h4
                  class="text-sm font-semibold text-[#1E2A78]"
                >
                  {{ 'FINANCIAL_DATA.STATEMENT_TYPE_BY_YEAR' | translate }}
                </h4>

                <p class="mt-0.5 text-xs text-gray-500">
                  {{ 'FINANCIAL_DATA.STATEMENT_TYPE_HINT' | translate }}
                </p>
              </div>
            </div>

            <div
              class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            >
              @for (year of years; track year) {
                <div
                  class="rounded-xl border border-indigo-100 bg-white p-3 shadow-sm"
                >
                  <div
                    class="mb-2 flex items-center justify-between gap-2"
                  >
                    <span
                      class="text-sm font-bold text-[#1E2A78]"
                    >
                      {{ year }}
                    </span>

                    <button
                      type="button"
                      (click)="removeYear(year)"
                      class="flex h-7 w-7 items-center justify-center rounded-lg text-xs font-bold text-red-500 transition hover:bg-red-50 hover:text-red-700"
                      [title]="'FINANCIAL_DATA.REMOVE_YEAR' | translate"
                    >
                      ✕
                    </button>
                  </div>

                  <select
                    [ngModel]="financialTypeByYear[year]"
                    (ngModelChange)="
                      onYearFinancialTypeChange(
                        year,
                        $event
                      )
                    "
                    [name]="'financialType_' + year"
                    class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                  >
                    <option [ngValue]="null">
                      {{ 'FINANCIAL_DATA.SELECT_TYPE' | translate }}
                    </option>

                    <option [ngValue]="0">
                      {{ 'FINANCIAL_DATA.AUDITED' | translate }}
                    </option>

                    <option [ngValue]="1">
                      {{ 'FINANCIAL_DATA.UNAUDITED' | translate }}
                    </option>
                  </select>
                </div>
              }
            </div>
          </div>
        }
      </div>

      <!-- Empty State -->
      @if (financialSections.length === 0) {
        <div
          class="p-10 text-center text-gray-500"
        >
          {{ 'FINANCIAL_DATA.NO_ACTIVE_CATEGORIES' | translate }}
        </div>
      }

      <!-- Financial Sections -->
      <div class="p-5 space-y-4">
        @for (section of financialSections; track section.fncTypSeq; let isLastSection = $last) {
          <div
            class="border border-gray-200 rounded-xl overflow-hidden"
          >
            <!-- Section Header -->
            <div
              class="flex items-center justify-between gap-4 bg-indigo-50 px-4 py-3 cursor-pointer hover:bg-indigo-100 transition"
              (click)="toggle(section)"
            >
              <div class="flex items-center gap-3 min-w-0">
                <input
                  type="checkbox"
                  class="w-4 h-4 shrink-0 accent-[#1E2A78]"
                  [(ngModel)]="section.checked"
                  (ngModelChange)="toggleSectionRows(section)"
                  (click)="$event.stopPropagation()"
                />

                <div class="min-w-0">
                  <span class="font-semibold text-indigo-800">
                    {{ section.title | translate }}
                  </span>

                  @if (section.description) {
                    <p
                      class="text-xs text-indigo-600 mt-0.5 truncate"
                    >
                      {{ section.description | translate }}
                    </p>
                  }
                </div>
              </div>

              <span class="text-xl text-gray-500 shrink-0">
                {{ section.expanded ? '−' : '+' }}
              </span>
            </div>

            <!-- Section Body -->
            @if (section.expanded) {
              <div class="bg-white">
                <!-- Years Header -->
                <div class="overflow-x-auto">
                  <div class="min-w-[820px]">
                    <div
                      class="grid bg-gray-50 border-b border-gray-200 px-4 py-3 text-sm text-gray-600 font-medium gap-3"
                      [style.grid-template-columns]="'minmax(260px, 1.2fr) repeat(' + years.length + ', minmax(190px, 1fr))'"
                    >
                      <div class="flex items-center">
                        {{ 'FINANCIAL_DATA.FINANCIAL_ITEM' | translate }}
                      </div>

                      @for (year of years; track year) {
                        <div
                          class="flex items-center justify-end gap-2"
                        >
                          <span
                            class="font-semibold text-gray-700"
                          >
                            {{ year }}
                          </span>

                          <span
                            class="rounded-full px-2.5 py-1 text-[11px] font-semibold"
                            [ngClass]="{
                              'bg-emerald-100 text-emerald-700':
                                financialTypeByYear[year] === 0,
                              'bg-amber-100 text-amber-700':
                                financialTypeByYear[year] === 1,
                              'bg-gray-200 text-gray-600':
                                financialTypeByYear[year] === null ||
                                financialTypeByYear[year] === undefined
                            }"
                          >
                            {{
                              getFinancialTypeLabelKey(
                                financialTypeByYear[year]
                              ) | translate
                            }}
                          </span>
                        </div>
                      }
                    </div>

                    <!-- Rows -->
                    @for (row of section.rows; track row.fncValSeq) {
                      <div
                        class="grid gap-3 items-center px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50"
                        [style.grid-template-columns]="'minmax(260px, 1.2fr) repeat(' + years.length + ', minmax(190px, 1fr))'"
                      >
                        <!-- Row Label -->
                        <div class="flex items-center gap-3 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            class="w-4 h-4 shrink-0 accent-[#1E2A78]"
                            [(ngModel)]="row.checked"
                          />

                          <span>
                            {{ row.label | translate }}
                          </span>
                        </div>

                        <!-- Value Inputs -->
                        @for (year of years; track year) {
                          <div>
                            <input
                              type="text"
                              appAmountInput
                              [amountAllowNegative]="true"
                              [(ngModel)]="demographicModel[getFieldKey(row.key, year)]"
                              [disabled]="!row.checked"
                              [placeholder]="'FINANCIAL_DATA.ENTER_VALUE' | translate"
                              class="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-end text-sm outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                            />
                          </div>
                        }
                      </div>
                    }

                    <!-- Total row (computed in code, not from Excel) -->
                    @if (section.rows.length) {
                      <div
                        class="grid gap-3 items-center border-t-2 border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-[#1E2A78]"
                        [style.grid-template-columns]="'minmax(260px, 1.2fr) repeat(' + years.length + ', minmax(190px, 1fr))'"
                      >
                        <div class="flex items-center">
                          {{ 'FINANCIAL_DATA.TOTAL_SECTION' | translate: { section: section.title } }}
                        </div>

                        @for (year of years; track year) {
                          <div
                            class="pe-3 text-end"
                          >
                            {{ getSectionTotal(section, year) | number : '1.0-0' }}
                          </div>
                        }
                      </div>
                    }

                    <!-- Grand totals (only after the last section) -->
                    @if (isLastSection) {
                      <!-- Total Liabilities = Current + Non-Current Liabilities -->
                      <div
                        class="grid gap-3 items-center border-t-2 border-gray-300 bg-indigo-50/50 px-4 py-3 text-sm font-semibold text-[#1E2A78]"
                        [style.grid-template-columns]="'minmax(260px, 1.2fr) repeat(' + years.length + ', minmax(190px, 1fr))'"
                      >
                        <div class="flex items-center font-bold">
                          {{ 'FINANCIAL_DATA.TOTAL_LIABILITIES' | translate }}
                        </div>

                        @for (year of years; track year) {
                          <div
                            class="pe-3 text-end"
                          >
                            {{ getTotalLiabilities(year) | number : '1.0-0' }}
                          </div>
                        }
                      </div>

                      <!-- Total Liabilities and Equity = Total Liabilities + Equity -->
                      <div
                        class="grid gap-3 items-center border-t border-gray-300 bg-indigo-50 px-4 py-3 text-sm font-bold text-[#1E2A78]"
                        [style.grid-template-columns]="'minmax(260px, 1.2fr) repeat(' + years.length + ', minmax(190px, 1fr))'"
                      >
                        <div class="flex items-center font-extrabold">
                          {{ 'FINANCIAL_DATA.TOTAL_LIABILITIES_AND_EQUITY' | translate }}
                        </div>

                        @for (year of years; track year) {
                          <div
                            class="pe-3 text-end"
                          >
                            {{ getTotalLiabilitiesAndEquity(year) | number : '1.0-0' }}
                          </div>
                        }
                      </div>
                    }
                  </div>
                </div>

                <!-- No Rows -->
                @if (section.rows.length === 0) {
                  <div
                    class="p-6 text-center text-sm text-gray-500"
                  >
                    {{ 'FINANCIAL_DATA.NO_ITEMS_CONFIGURED' | translate }}
                  </div>
                }
              </div>
            }
          </div>
        }
      </div>
    </div>

  </div>
} @else {
  <!-- Preview Mode -->
  <div class="space-y-6">
    <!-- Financial Information Preview -->
    <div class="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <h3 class="text-lg font-semibold text-[#1E2A78] mb-5">
        {{ 'FINANCIAL_DATA.FINANCIAL_INFORMATION' | translate }}
      </h3>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-5 text-sm">
        <div>
          <p class="text-gray-500 mb-1">{{ 'FINANCIAL_DATA.REPORTING_CURRENCY' | translate }}</p>
          <p class="font-medium text-gray-800">
            {{ getReportingCurrencyLabel(demographicModel.reportingCurrency) }}
          </p>
        </div>

        <div>
          <p class="text-gray-500 mb-1">{{ 'FINANCIAL_DATA.FINANCIAL_YEAR_END' | translate }}</p>
          <p class="font-medium text-gray-800">
            {{ demographicModel.financialYearEnd || '-' }}
          </p>
        </div>

        <div>
          <p class="text-gray-500 mb-1">{{ 'FINANCIAL_DATA.CONSOLIDATED_FINANCIALS' | translate }}</p>
          <p class="font-medium text-gray-800">
            {{
              demographicModel.isConsolidated === true ||
              demographicModel.isConsolidated === 'true' ||
              demographicModel.isConsolidated === 1
                ? ('COMMON.YES' | translate)
                : ('COMMON.NO' | translate)
            }}
          </p>
        </div>
      </div>
    </div>

    <!-- Financial Data Preview -->
    @for (section of financialSections; track section.fncTypSeq) {
      <div
        class="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden"
      >
        <div class="px-6 py-4 bg-indigo-50 border-b border-indigo-100">
          <h3 class="font-semibold text-indigo-800">
            {{ section.title | translate }}
          </h3>

          @if (section.description) {
            <p class="text-xs text-indigo-600 mt-1">
              {{ section.description | translate }}
            </p>
          }
        </div>

        <div class="overflow-x-auto">
          <table class="w-full min-w-[700px]">
            <thead>
              <tr class="bg-[#1E2A78] text-white">
                <th class="px-5 py-3 text-start text-sm font-semibold">
                  {{ 'FINANCIAL_DATA.FINANCIAL_ITEM' | translate }}
                </th>

                @for (year of years; track year) {
                  <th
                    class="px-5 py-3 text-end text-sm font-semibold"
                  >
                    <div>{{ year }}</div>
                    <div class="text-xs font-normal opacity-80 mt-1">
                      {{ getFinancialTypeLabelKey(financialTypeByYear[year]) | translate }}
                    </div>
                  </th>
                }
              </tr>
            </thead>

            <tbody>
              @for (row of section.rows; track row.fncValSeq) {
                @if (row.checked) {
                  <tr
                    class="border-b border-gray-100 last:border-b-0"
                  >
                    <td class="px-5 py-4 text-sm text-gray-700">
                      {{ row.label | translate }}
                    </td>

                    @for (year of years; track year) {
                      <td
                        class="px-5 py-4 text-sm text-end text-gray-700"
                      >
                        {{ (demographicModel[getFieldKey(row.key, year)] | amount) || '-' }}
                      </td>
                    }
                  </tr>
                }
              }

              @if (!section.checked) {
                <tr>
                  <td
                    [attr.colspan]="years.length + 1"
                    class="px-5 py-6 text-center text-sm text-gray-500"
                  >
                    {{ 'FINANCIAL_DATA.NO_DATA_SELECTED' | translate }}
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    }
  </div>
}
